/**
 * Issue Dialog Component
 * Modal for logging test issues with screenshot upload support.
 * Auto-populates project context from the test case and pushes to SharePoint.
 */

import { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { TestCase, IssueSeverity } from '@/types/domain';
import { issuesApi, type Issue } from '@/services/issues';

// =============================================================================
// Schema & Types
// =============================================================================

const SEVERITY_OPTIONS: { value: IssueSeverity; label: string }[] = [
  { value: '1_Critical', label: 'Critical' },
  { value: '2_High', label: 'High' },
  { value: '3_Medium', label: 'Medium' },
  { value: '4_Low', label: 'Low' },
];

const issueSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  severity: z.enum(['1_Critical', '2_High', '3_Medium', '4_Low'], {
    required_error: 'Please select a severity level',
  }),
});

type IssueFormData = z.infer<typeof issueSchema>;

interface IssueDraftStorage {
  version: 1;
  savedAt: string;
  title: string;
  description: string;
  severity: IssueSeverity;
}

interface IssueDialogProps {
  testCase: TestCase;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIssueCreated: () => void;
}

const ISSUE_DRAFT_PREFIX = 'lotus-issue-draft';

function getIssueDraftKey(testCaseId: string): string {
  return `${ISSUE_DRAFT_PREFIX}:${testCaseId}`;
}

function loadIssueDraft(testCaseId: string): IssueDraftStorage | null {
  try {
    const raw = localStorage.getItem(getIssueDraftKey(testCaseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IssueDraftStorage;
    if (parsed.version !== 1) return null;
    if (!parsed.title || !parsed.severity) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveIssueDraft(testCaseId: string, draft: Omit<IssueDraftStorage, 'version' | 'savedAt'>): void {
  try {
    const payload: IssueDraftStorage = {
      ...draft,
      version: 1,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(getIssueDraftKey(testCaseId), JSON.stringify(payload));
  } catch {
    // Best-effort persistence only.
  }
}

function clearIssueDraft(testCaseId: string): void {
  try {
    localStorage.removeItem(getIssueDraftKey(testCaseId));
  } catch {
    // Ignore storage errors.
  }
}
// =============================================================================
// Component
// =============================================================================

export function IssueDialog({
  testCase,
  open,
  onOpenChange,
  onIssueCreated,
}: IssueDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdIssue, setCreatedIssue] = useState<Issue | null>(null);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [, setScreenshotPreview] = useState<string | null>(null);
  const [, setUploadProgress] = useState(0);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<IssueFormData>({
    resolver: zodResolver(issueSchema),
    defaultValues: {
      title: '',
      description: '',
      severity: '3_Medium',
    },
  });

  useEffect(() => {
    if (!open || createdIssue) return;

    const draft = loadIssueDraft(testCase.id);
    reset({
      title: draft?.title || '',
      description: draft?.description || '',
      severity: draft?.severity || '3_Medium',
    });
  }, [open, createdIssue, testCase.id, reset]);

  useEffect(() => {
    if (!open || createdIssue) return;

    const subscription = watch((values) => {
      saveIssueDraft(testCase.id, {
        title: values.title || '',
        description: values.description || '',
        severity: (values.severity as IssueSeverity) || '3_Medium',
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, open, createdIssue, testCase.id]);

  const handleDone = () => {
    clearIssueDraft(testCase.id);
    reset();
    removeScreenshot();
    setCreatedIssue(null);
    onOpenChange(false);
    onIssueCreated();
  };

  // -------------------------------------------------------------------------
  // Screenshot handling
  // -------------------------------------------------------------------------

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setScreenshot(file);
    const reader = new FileReader();
    reader.onload = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const named = new File([file], `screenshot-${Date.now()}.png`, {
              type: file.type,
            });
            processFile(named);
          }
          break;
        }
      }
    },
    [processFile],
  );

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    setUploadProgress(0);
  };

  const uploadScreenshot = async (): Promise<string | undefined> => {
    if (!screenshot) return undefined;

    try {
      setUploadProgress(10);

      // 1. Get presigned URL from backend
      const { upload_url, s3_key } = await issuesApi.getScreenshotUploadUrl({
        filename: screenshot.name,
        size: screenshot.size,
        content_type: screenshot.type,
      });

      setUploadProgress(30);

      // 2. Upload to S3
      await issuesApi.uploadScreenshot(upload_url, screenshot);

      setUploadProgress(100);
      return s3_key;
    } catch (error) {
      console.error('Failed to upload screenshot:', error);
      toast.error('Failed to upload screenshot');
      return undefined;
    }
  };

  // -------------------------------------------------------------------------
  // Form submission
  // -------------------------------------------------------------------------

  const handleFormSubmit = async (data: IssueFormData) => {
    try {
      setIsSubmitting(true);

      // Upload screenshot if present
      let screenshot_s3_key: string | undefined;
      if (screenshot) {
        screenshot_s3_key = await uploadScreenshot();
      }

      // Create issue via API
      const issue = await issuesApi.create({
        test_case_id: testCase.id,
        test_case_user_id: testCase.user_id,
        title: data.title,
        description: data.description,
        severity: data.severity,
        screenshot_s3_key,
        sharepoint_file_url: testCase.sharepoint_file_url,
      });

      clearIssueDraft(testCase.id);
      console.log('[IssueDialog] Created issue response:', issue);
      // Show success state inside dialog
      setCreatedIssue(issue);
    } catch (error) {
      console.error('Failed to create issue:', error);
      const message = error instanceof Error
        ? error.message
        : 'Failed to create issue. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Success state
  // -------------------------------------------------------------------------

  if (createdIssue) {
    const severityLabel = SEVERITY_OPTIONS.find(
      (o) => o.value === createdIssue.severity,
    )?.label ?? createdIssue.severity;

    return (
      <Dialog open={open} onOpenChange={() => handleDone()}>
        <DialogContent className="max-w-lg">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold">Issue Created</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your issue has been logged and will be tracked on the Action List.
              </p>
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium">{createdIssue.title}</p>
              <Badge
                className={cn(
                  'text-xs shrink-0',
                  createdIssue.severity === '1_Critical' && 'bg-red-100 text-red-700 border-red-200',
                  createdIssue.severity === '2_High' && 'bg-orange-100 text-orange-700 border-orange-200',
                  createdIssue.severity === '3_Medium' && 'bg-yellow-100 text-yellow-700 border-yellow-200',
                  createdIssue.severity === '4_Low' && 'bg-blue-100 text-blue-700 border-blue-200',
                )}
              >
                {severityLabel}
              </Badge>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                <span className="font-medium text-foreground">Step: </span>
                {testCase.step_number} — {testCase.description}
              </p>
              {testCase.scenario_name && (
                <p>
                  <span className="font-medium text-foreground">Test Script: </span>
                  {testCase.scenario_name}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs">
              {createdIssue.sharepoint_synced ? (
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Synced to SharePoint
                </Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                  Pending SharePoint sync
                </Badge>
              )}
            </div>

            {createdIssue.sharepoint_fields_skipped && createdIssue.sharepoint_fields_skipped.length > 0 && (
              <p className="text-xs text-amber-600">
                SP fields not resolved: {createdIssue.sharepoint_fields_skipped.join(', ')}
              </p>
            )}
            {createdIssue.sharepoint_rest_fields_failed && createdIssue.sharepoint_rest_fields_failed.length > 0 && (
              <p className="text-xs text-amber-600">
                SP REST fields failed: {createdIssue.sharepoint_rest_fields_failed.join(', ')}
              </p>
            )}
            {createdIssue.sharepoint_rest_error_detail && (
              <p className="text-xs text-amber-600">
                SP detail: {createdIssue.sharepoint_rest_error_detail}
              </p>
            )}
            {createdIssue.sharepoint_error && (
              <p className="text-xs text-red-600">
                SP error: {createdIssue.sharepoint_error}
              </p>
            )}

            {createdIssue.sharepoint_web_url && (
              <a
                href={createdIssue.sharepoint_web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View on SharePoint Action List
              </a>
            )}
          </div>

          <DialogFooter>
            <Button onClick={handleDone}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // -------------------------------------------------------------------------
  // Form state
  // -------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" onPaste={handlePaste}>
        <DialogHeader>
          <DialogTitle>Log Test Issue</DialogTitle>
          <DialogDescription>
            Report an issue encountered during test execution. This will be
            pushed to the SharePoint Action List.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {/* Context Panel (read-only, auto-populated) */}
          <div className="p-3 rounded-lg bg-muted space-y-1">
            <p className="text-sm font-medium mb-1.5">Test Context</p>
            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
              {testCase.project_name && (
                <p>
                  <span className="font-medium text-foreground">Project: </span>
                  {testCase.project_name}
                  {testCase.project_number && ` (${testCase.project_number})`}
                </p>
              )}
              {testCase.testing_phase && (
                <p>
                  <span className="font-medium text-foreground">Phase: </span>
                  {testCase.testing_phase}
                </p>
              )}
              {testCase.sharepoint_file_name && (
                <p>
                  <span className="font-medium text-foreground">File: </span>
                  {testCase.sharepoint_file_name}
                </p>
              )}
              {testCase.testscript_id && (
                <p>
                  <span className="font-medium text-foreground">Test Script: </span>
                  {testCase.testscript_id}
                  {testCase.scenario_name && ` — ${testCase.scenario_name}`}
                </p>
              )}
              {testCase.tester && (
                <p>
                  <span className="font-medium text-foreground">Domain: </span>
                  {testCase.tester}
                </p>
              )}
              <p>
                <span className="font-medium text-foreground">Step: </span>
                {testCase.step_number} — {testCase.description}
              </p>
            </div>
          </div>

          {/* Issue Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Issue Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Brief description of the issue"
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label>Severity *</Label>
            <Controller
              name="severity"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={SEVERITY_OPTIONS}
                  placeholder="Select severity"
                  searchPlaceholder="Search severity..."
                />
              )}
            />
            {errors.severity && (
              <p className="text-sm text-red-600">{errors.severity.message}</p>
            )}
          </div>

          {/* Issue Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Detailed description of what went wrong, steps to reproduce, etc."
              rows={5}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Issue
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
